import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  buildDefaultClosureSummary,
  readClosureSummary,
  type ClosureSummaryFields,
} from '../../domain/safetyCaseClosure/safetyCaseClosure';
import { reportsApi } from '../../services/reportsApi';
import type { SafetyCaseRecord } from '../../services/reportsApi';
import {
  type SafetyCaseAttestationSaveHandler,
  useRegisterSafetyCaseSaveHandler,
} from '../../domain/safetyCases/safetyCaseAttestationSave';

type ClosureSummarySectionProps = {
  safetyCaseId: string;
  item: SafetyCaseRecord;
  readOnly?: boolean;
  workflowImpactSummary?: Record<string, unknown> | null;
  onSaved?: (fields: ClosureSummaryFields) => void;
  hideSaveButton?: boolean;
  saveHandlerRef?: React.MutableRefObject<SafetyCaseAttestationSaveHandler | null>;
};

const FIELD_CONFIG: Array<{
  key: keyof ClosureSummaryFields;
  label: string;
}> = [
  { key: 'what_was_observed', label: 'What was observed' },
  { key: 'what_changed', label: 'What changed' },
  { key: 'what_followup_showed', label: 'What follow-up showed' },
  { key: 'what_remains_open', label: 'What remains open' },
  { key: 'monitoring_note', label: 'Monitoring note' },
];

function fieldsFromItem(
  item: SafetyCaseRecord,
  workflowImpactSummary?: Record<string, unknown> | null,
): ClosureSummaryFields {
  const saved = readClosureSummary(item.scope);
  if (Object.keys(saved).length) return saved;
  return buildDefaultClosureSummary({
    scope: item.scope || undefined,
    safetyCaseStatus: item.safety_case_status,
    impactSummary: workflowImpactSummary as any,
  });
}

export const ClosureSummarySection: React.FC<ClosureSummarySectionProps> = ({
  safetyCaseId,
  item,
  readOnly = false,
  workflowImpactSummary,
  onSaved,
  hideSaveButton = false,
  saveHandlerRef,
}) => {
  const [fields, setFields] = useState<ClosureSummaryFields>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const defaults = useMemo(
    () => fieldsFromItem(item, workflowImpactSummary),
    [item, workflowImpactSummary],
  );

  useEffect(() => {
    setFields(defaults);
    setSaveError(null);
    setSaveNotice(null);
  }, [defaults]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    if (!hideSaveButton) setSaveNotice(null);
    try {
      const response = await reportsApi.patchClosureSummary(safetyCaseId, {
        what_was_observed: String(fields.what_was_observed || '').trim() || null,
        what_changed: String(fields.what_changed || '').trim() || null,
        what_followup_showed: String(fields.what_followup_showed || '').trim() || null,
        what_remains_open: String(fields.what_remains_open || '').trim() || null,
        monitoring_note: String(fields.monitoring_note || '').trim() || null,
      });
      const saved = readClosureSummary(response.safety_case?.scope || response.finding?.scope);
      onSaved?.(saved);
      if (!hideSaveButton) setSaveNotice('Closure summary saved.');
    } catch (error: any) {
      setSaveError(error?.message || 'Unable to save closure summary right now.');
      throw error;
    } finally {
      setSaving(false);
    }
  }, [fields, hideSaveButton, onSaved, safetyCaseId]);

  useRegisterSafetyCaseSaveHandler(readOnly ? undefined : saveHandlerRef, handleSave);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }} data-testid="closure-summary-section">
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
          Closure Summary
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Summarize the closure loop. This should describe evidence, action, follow-up, and any remaining monitoring. Do not overstate causality.
        </Typography>
        <Stack spacing={2}>
          {FIELD_CONFIG.map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              value={String(fields[field.key] || '')}
              onChange={(event) => setFields((current) => ({
                ...current,
                [field.key]: event.target.value,
              }))}
              fullWidth
              multiline
              minRows={2}
              size="small"
            />
          ))}
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          {saveNotice ? <Alert severity="success">{saveNotice}</Alert> : null}
          <Box>
          {!readOnly && !hideSaveButton ? (
            <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving…' : 'Save closure summary'}
            </Button>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ClosureSummarySection;
